import React, { useState, useContext, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import ChatWidget from '../chat/ChatWidget';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Fab,
  Tooltip,
  Badge,
  Divider,
  Stack,
  Paper,
  Fade,
  Zoom,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  SmartToy as SmartToyIcon,
  EventNote as EventNoteIcon,
  CreditCard as CreditCardIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  Description as DescriptionIcon,
  Chat as ChatIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  ChevronRight as ChevronRightIcon,
  Mail as MailIcon,
  Assessment as AssessmentIcon,
  Folder as FolderIcon,
  Search as SearchIcon,
  KeyboardCommandKey as CommandIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  MoreVert as MoreVertIcon,
  AccountBalanceWallet as AccountBalanceIcon,
  ReceiptLong as ReceiptIcon,
  Lock as VaultIcon,
  AccountBalance as BankIcon,
  Flag as FlagIcon,
  CloudDownload as ScrapeIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { AuthContext } from '../../App';
import api from '../../services/api';
import { cardService } from '../../services/cards';

const drawerWidth = 280;
const collapsedDrawerWidth = 80;

// User menu items (for regular users)
const userMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Smart Recommendations', icon: <SmartToyIcon />, path: '/smart-recommendations' },
  { text: 'Planning', icon: <EventNoteIcon />, path: '/planning' },
  { text: 'Cards', icon: <CreditCardIcon />, path: '/cards' },
  { text: 'Transactions', icon: <ReceiptIcon />, path: '/transactions' },
  { text: 'Gmail Connect', icon: <MailIcon />, path: '/gmail-connect' },
  { text: 'Expense Dashboard', icon: <AccountBalanceIcon />, path: '/expense-dashboard' },
  { text: 'Bill Vault', icon: <VaultIcon />, path: '/bill-vault' },
  { text: 'Upload Statement', icon: <DescriptionIcon />, path: '/upload-statement' },
  { text: 'Profile', icon: <PersonIcon />, path: '/profile' },
];

// Admin menu items (for admin users only) - single entry to go to admin
const adminMenuItems = [
  { text: 'Admin Panel', icon: <AdminPanelSettingsIcon />, path: '/admin' },
];

// Admin sub-menu: shown in sidebar when on /admin (tabs as sidebar items)
const adminSubMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin?tab=0', tab: 0 },
  { text: 'Banks', icon: <BankIcon />, path: '/admin?tab=1', tab: 1 },
  { text: 'Cards', icon: <CreditCardIcon />, path: '/admin?tab=2', tab: 2 },
  { text: 'T&T Banks', icon: <FlagIcon />, path: '/admin?tab=3', tab: 3 },
  { text: 'Scraping', icon: <ScrapeIcon />, path: '/admin?tab=4', tab: 4 },
  { text: 'Gmail Expenses', icon: <MailIcon />, path: '/admin?tab=5', tab: 5 },
];

// When normal user has no cards yet: only show Cards (so they can add first card)
const userMenuItemsNoCards = [
  { text: 'Cards', icon: <CreditCardIcon />, path: '/cards' },
];

const Layout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState<null | HTMLElement>(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchFocused, setSearchFocused] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const auth = useContext(AuthContext);

  const isAdminPage = location.pathname === '/admin' || location.pathname.startsWith('/admin/');
  const adminTab = isAdminPage ? (searchParams.get('tab') || '0') : null;

  const notificationsMenuOpen = Boolean(notificationsAnchorEl);
  const settingsMenuOpen = Boolean(settingsAnchorEl);

  // Auto-collapse on tablet
  useEffect(() => {
    if (isTablet) {
      setCollapsed(true);
    } else if (!isMobile) {
      setCollapsed(false);
    }
  }, [isTablet, isMobile]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSettingsClick = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleNotificationsClick = async (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchorEl(event.currentTarget);
    setNotificationsLoading(true);
    try {
      const response = await api.get('/notifications/');
      setNotifications(response.data || []);
    } catch (err) {
      console.error('Failed to load notifications', err);
    } finally {
      setNotificationsLoading(false);
    }
  };

  const handleNotificationClick = async (notification: any) => {
    try {
      if (!notification.is_read) {
        await api.patch(`/notifications/${notification.id}/`, { is_read: true });
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n)),
        );
      }
    } catch (err) {
      console.error('Failed to mark notification as read', err);
    }
  };

  const unreadNotificationsCount = notifications.filter((n) => !n.is_read).length;

  const handleLogout = () => {
    auth?.logout();
    handleMenuClose();
    navigate('/login');
  };

  const isAdmin = !!auth?.user &&
    (auth.user.is_staff ||
      (auth.user as any).user_type === 'ADMIN' ||
      (auth.user as any).is_superuser);

  const { data: userCards = [] } = useQuery({
    queryKey: ['userCards'],
    queryFn: cardService.getUserCards,
    enabled: !!auth?.user && !isAdmin,
  });

  const hasCards = (userCards?.length ?? 0) > 0;
  // When admin is on /admin, show admin sub-menu (tabs) in sidebar; otherwise show single "Admin Panel" or user menu
  const menuItems = isAdmin && isAdminPage
    ? adminSubMenuItems
    : isAdmin
      ? adminMenuItems
      : hasCards
        ? userMenuItems
        : userMenuItemsNoCards;
  const currentDrawerWidth = isMobile ? drawerWidth : (collapsed ? collapsedDrawerWidth : drawerWidth);

  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#ffffff',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Logo/Brand Section - Modern Glass Effect with Dark Gradient */}
      <Box
        sx={{
          p: collapsed ? 2 : 3,
          borderBottom: '1px solid',
          borderColor: alpha(theme.palette.divider, 0.1),
          background: 'linear-gradient(135deg, #153da1ff 0%, #31416eff 100%)',
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 0% 50%, rgba(99, 102, 241, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Stack 
          direction="row" 
          spacing={collapsed ? 0 : 2} 
          alignItems="center"
          sx={{
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
        >
          <Box
            sx={{
              width: collapsed ? 44 : 48,
              height: collapsed ? 44 : 48,
              borderRadius: 2.5,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 16px -4px rgba(99, 102, 241, 0.2)',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: '0 12px 24px -8px rgba(99, 102, 241, 0.3)',
              },
            }}
            onClick={() => navigate('/dashboard')}
          >
            <CreditCardIcon sx={{ color: 'white', fontSize: collapsed ? 22 : 26 }} />
          </Box>
          
          {!collapsed && (
            <Fade in={!collapsed} timeout={500}>
              <Box
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate('/dashboard')}
              >
                <Typography 
                  variant="h6" 
                  fontWeight={700} 
                  color="white" 
                  sx={{ 
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em',
                    fontSize: '1.25rem',
                  }}
                >
                  Card Optimizer
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: alpha('#ffffff', 0.7),
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Intelligent Spending
                </Typography>
              </Box>
            </Fade>
          )}
        </Stack>
      </Box>

      {/* Navigation Menu - Modern & Clean */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 3, px: collapsed ? 1.5 : 2 }}>
        <List sx={{ px: 0 }}>
          {menuItems.map((item) => {
            const isActive = (location.pathname + (location.search || '')) === item.path ||
              ((item as any).tab != null && location.pathname === '/admin' && (searchParams.get('tab') === String((item as any).tab) || (searchParams.get('tab') == null && (item as any).tab === 0)));
            return (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.75 }}>
                <Tooltip
                  title={collapsed ? item.text : ''}
                  placement="right"
                  arrow
                  disableHoverListener={!collapsed}
                >
                  <ListItemButton
                    onClick={() => {
                      navigate(item.path);
                      if (isMobile) setMobileOpen(false);
                    }}
                    sx={{
                      borderRadius: 2.5,
                      minHeight: 48,
                      px: collapsed ? 2 : 2.5,
                      py: 1.25,
                      bgcolor: isActive 
                        ? alpha(theme.palette.primary.main, 0.08)
                        : 'transparent',
                      color: isActive 
                        ? theme.palette.primary.main
                        : alpha(theme.palette.text.secondary, 0.8),
                      '&:hover': {
                        bgcolor: isActive 
                          ? alpha(theme.palette.primary.main, 0.12)
                          : alpha(theme.palette.primary.main, 0.04),
                        color: isActive 
                          ? theme.palette.primary.main
                          : theme.palette.text.primary,
                      },
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      '&::before': isActive
                        ? {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 4,
                            height: 28,
                            bgcolor: theme.palette.primary.main,
                            borderRadius: '0 4px 4px 0',
                            boxShadow: `0 0 12px ${alpha(theme.palette.primary.main, 0.4)}`,
                          }
                        : {},
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isActive 
                          ? theme.palette.primary.main
                          : alpha(theme.palette.text.secondary, 0.7),
                        minWidth: collapsed ? 'auto' : 40,
                        mr: collapsed ? 0 : 2,
                        '& svg': {
                          fontSize: 22,
                          transition: 'all 0.2s ease',
                        },
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    
                    {!collapsed && (
                      <>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: '0.9375rem',
                            fontWeight: isActive ? 600 : 500,
                            sx: {
                              transition: 'all 0.2s ease',
                            },
                          }}
                        />
                        {isActive && (
                          <Zoom in={isActive}>
                            <ChevronRightIcon 
                              sx={{ 
                                fontSize: 18, 
                                color: theme.palette.primary.main,
                                ml: 'auto',
                              }} 
                            />
                          </Zoom>
                        )}
                      </>
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>

        {!collapsed && (
          <Divider sx={{ my: 3, mx: 2, borderColor: alpha(theme.palette.divider, 0.6) }} />
        )}

        {/* Quick Actions for collapsed mode */}
        {collapsed && (
          <Stack spacing={1.5} sx={{ mt: 3, px: 1 }}>
            <Tooltip title="New Transaction" placement="right" arrow>
              <IconButton
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.16),
                  },
                  width: 48,
                  height: 48,
                  mx: 'auto',
                }}
              >
                <CreditCardIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Box>

      {/* User Section - Modern Card Style */}
      <Box 
        sx={{ 
          p: collapsed ? 2 : 2.5,
          borderTop: '1px solid',
          borderColor: alpha(theme.palette.divider, 0.1),
          background: alpha(theme.palette.background.paper, 0.6),
          backdropFilter: 'blur(8px)',
        }}
      >
        <Stack 
          direction="row" 
          spacing={collapsed ? 0 : 1.5} 
          alignItems="center"
          sx={{
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
        >
          <Avatar
            sx={{
              width: collapsed ? 44 : 42,
              height: collapsed ? 44 : 42,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              fontSize: '0.875rem',
              fontWeight: 600,
              border: '2px solid white',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            }}
          >
            {auth?.user?.first_name?.[0] || auth?.user?.username?.[0] || 'U'}
          </Avatar>
          
          {!collapsed && (
            <Fade in={!collapsed} timeout={500}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: theme.palette.text.primary,
                    fontSize: '0.9375rem',
                  }}
                >
                  {auth?.user?.first_name || auth?.user?.username || 'User'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.75rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {auth?.user?.email || ''}
                </Typography>
              </Box>
            </Fade>
          )}
        </Stack>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh', 
      bgcolor: '#f6f8fa',
      background: 'linear-gradient(145deg, #f6f8fa 0%, #f0f2f5 100%)',
    }}>
      {/* Premium AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: alpha('#ffffff', 0.85),
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid',
          borderColor: alpha(theme.palette.divider, 0.08),
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Toolbar sx={{ px: { xs: 2, md: 3 }, minHeight: '72px !important' }}>
          <IconButton
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              display: 'flex',
              color: theme.palette.text.secondary,
              bgcolor: alpha(theme.palette.primary.main, 0),
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
              },
              transition: 'all 0.2s ease',
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Page Title - Dynamic based on route */}
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 600,
              fontSize: '1.25rem',
              color: theme.palette.text.primary,
              display: { xs: 'none', sm: 'block' },
              letterSpacing: '-0.01em',
            }}
          >
            {isAdminPage && adminTab != null
              ? (menuItems.find((item: any) => String(item.tab) === adminTab)?.text ?? 'Admin')
              : (menuItems.find(item => item.path === location.pathname)?.text || 'Dashboard')}
          </Typography>

          {/* Search Bar - Modern Design */}
          <Box 
            sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              justifyContent: 'center',
              px: 3,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                maxWidth: 480,
                width: '100%',
                px: 2,
                py: 0.75,
                bgcolor: searchFocused 
                  ? alpha(theme.palette.background.paper, 1)
                  : alpha(theme.palette.background.paper, 0.6),
                borderRadius: 3,
                border: '1px solid',
                borderColor: searchFocused
                  ? alpha(theme.palette.primary.main, 0.3)
                  : alpha(theme.palette.divider, 0.1),
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: alpha(theme.palette.background.paper, 0.9),
                  borderColor: alpha(theme.palette.primary.main, 0.2),
                },
              }}
            >
              <SearchIcon sx={{ color: alpha(theme.palette.text.secondary, 0.6), fontSize: 20, mr: 1 }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: alpha(theme.palette.text.secondary, 0.6),
                  flex: 1,
                }}
              >
                Search transactions, cards...
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <Chip
                  label="⌘"
                  size="small"
                  sx={{
                    height: 24,
                    bgcolor: alpha(theme.palette.text.primary, 0.06),
                    color: theme.palette.text.secondary,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label="K"
                  size="small"
                  sx={{
                    height: 24,
                    bgcolor: alpha(theme.palette.text.primary, 0.06),
                    color: theme.palette.text.secondary,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                />
              </Stack>
            </Paper>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            
            
            <IconButton
              onClick={handleNotificationsClick}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': { 
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                },
                transition: 'all 0.2s ease',
              }}
            >
              <Badge 
                badgeContent={unreadNotificationsCount} 
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    minWidth: 18,
                    height: 18,
                  },
                }}
              >
                <NotificationsIcon />
              </Badge>
            </IconButton>

            <IconButton
              onClick={handleSettingsClick}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': { 
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                },
                transition: 'all 0.2s ease',
              }}
            >
              <SettingsIcon />
            </IconButton>

            <IconButton
              onClick={handleAvatarClick}
              sx={{
                p: 0.5,
                ml: 0.5,
                border: '2px solid',
                borderColor: alpha(theme.palette.primary.main, 0.2),
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  transform: 'scale(1.05)',
                },
              }}
            >
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {auth?.user?.first_name?.[0] || auth?.user?.username?.[0] || 'U'}
              </Avatar>
            </IconButton>
          </Stack>

          {/* User Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            TransitionComponent={Fade}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 240,
                borderRadius: 2.5,
                boxShadow: '0 20px 40px -12px rgba(0,0,0,0.2)',
                border: '1px solid',
                borderColor: alpha(theme.palette.divider, 0.1),
                overflow: 'visible',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -8,
                  right: 16,
                  width: 16,
                  height: 16,
                  bgcolor: theme.palette.background.paper,
                  transform: 'rotate(45deg)',
                  borderLeft: '1px solid',
                  borderTop: '1px solid',
                  borderColor: alpha(theme.palette.divider, 0.1),
                },
              },
            }}
          >
            <Box sx={{ px: 2.5, py: 2 }}>
              <Typography variant="body2" fontWeight={600} color={theme.palette.text.primary}>
                {auth?.user?.first_name || auth?.user?.username || 'User'}
              </Typography>
              <Typography variant="caption" color={theme.palette.text.secondary}>
                {auth?.user?.email || ''}
              </Typography>
            </Box>
            <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.1) }} />
            <MenuItem 
              onClick={handleLogout}
              sx={{
                color: theme.palette.error.main,
                py: 1.5,
                '&:hover': { 
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                },
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
              </ListItemIcon>
              <Typography variant="body2" fontWeight={500}>Logout</Typography>
            </MenuItem>
          </Menu>

          {/* Settings Menu */}
          <Menu
            anchorEl={settingsAnchorEl}
            open={settingsMenuOpen}
            onClose={handleSettingsClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            TransitionComponent={Fade}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 200,
                borderRadius: 2.5,
                boxShadow: '0 20px 40px -12px rgba(0,0,0,0.2)',
                border: '1px solid',
                borderColor: alpha(theme.palette.divider, 0.1),
              },
            }}
          >
            <MenuItem sx={{ py: 1.5 }}>
              <ListItemIcon>
                <DarkModeIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="body2">Dark Mode</Typography>
              <Box sx={{ flex: 1 }} />
              <Chip 
                label="Soon" 
                size="small" 
                sx={{ 
                  height: 20, 
                  fontSize: '0.6rem',
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  color: theme.palette.warning.dark,
                }} 
              />
            </MenuItem>
            <MenuItem sx={{ py: 1.5 }}>
              <ListItemIcon>
                <CommandIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="body2">Keyboard Shortcuts</Typography>
            </MenuItem>
          </Menu>

          {/* Notifications Menu */}
          <Menu
            anchorEl={notificationsAnchorEl}
            open={notificationsMenuOpen}
            onClose={handleNotificationsClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            TransitionComponent={Fade}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 360,
                maxHeight: 480,
                borderRadius: 2.5,
                boxShadow: '0 20px 40px -12px rgba(0,0,0,0.2)',
                border: '1px solid',
                borderColor: alpha(theme.palette.divider, 0.1),
                overflow: 'visible',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -8,
                  right: 16,
                  width: 16,
                  height: 16,
                  bgcolor: theme.palette.background.paper,
                  transform: 'rotate(45deg)',
                  borderLeft: '1px solid',
                  borderTop: '1px solid',
                  borderColor: alpha(theme.palette.divider, 0.1),
                },
              },
            }}
          >
            <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body1" fontWeight={600} color={theme.palette.text.primary}>
                  Notifications
                </Typography>
                <Typography variant="caption" color={theme.palette.text.secondary}>
                  {unreadNotificationsCount > 0
                    ? `${unreadNotificationsCount} unread`
                    : 'You are all caught up'}
                </Typography>
              </Box>
              {unreadNotificationsCount > 0 && (
                <Chip 
                  label="Mark all read" 
                  size="small"
                  sx={{
                    height: 28,
                    fontSize: '0.7rem',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                  }}
                />
              )}
            </Box>
            <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.1) }} />
            <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
              {notificationsLoading && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color={theme.palette.text.secondary}>
                    Loading notifications...
                  </Typography>
                </Box>
              )}
              {!notificationsLoading && notifications.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color={theme.palette.text.secondary}>
                    No notifications yet
                  </Typography>
                </Box>
              )}
              {!notificationsLoading &&
                notifications.map((n) => (
                  <MenuItem
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    sx={{
                      px: 2.5,
                      py: 2,
                      alignItems: 'flex-start',
                      bgcolor: !n.is_read 
                        ? alpha(theme.palette.primary.main, 0.04)
                        : 'transparent',
                      borderBottom: '1px solid',
                      borderColor: alpha(theme.palette.divider, 0.05),
                      '&:hover': {
                        bgcolor: !n.is_read 
                          ? alpha(theme.palette.primary.main, 0.08)
                          : alpha(theme.palette.action.hover, 0.5),
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!n.is_read && (
                          <Box 
                            sx={{ 
                              width: 8, 
                              height: 8, 
                              borderRadius: '50%', 
                              bgcolor: theme.palette.primary.main,
                              boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.4)}`,
                            }} 
                          />
                        )}
                        <Typography
                          variant="body2"
                          fontWeight={n.is_read ? 500 : 600}
                          sx={{ color: theme.palette.text.primary }}
                        >
                          {n.title || 'Notification'}
                        </Typography>
                      </Box>
                      {n.message && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            maxWidth: 280,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            fontSize: '0.8125rem',
                            lineHeight: 1.5,
                          }}
                        >
                          {n.message}
                        </Typography>
                      )}
                      {n.created_at && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: alpha(theme.palette.text.secondary, 0.7),
                            fontSize: '0.7rem',
                          }}
                        >
                          {new Date(n.created_at).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
            </Box>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{
          width: { md: currentDrawerWidth },
          flexShrink: { md: 0 },
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              borderRight: 'none',
              boxShadow: '4px 0 20px -8px rgba(0,0,0,0.06)',
              top: 0,
              height: '100vh',
              zIndex: (theme) => theme.zIndex.drawer,
              transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              overflowX: 'hidden',
              bgcolor: 'background.paper',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          bgcolor: 'transparent',
          minHeight: '100vh',
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Box 
          sx={{ 
            pt: '88px', 
            px: { xs: 2.5, sm: 3, md: 4 }, 
            pb: 6,
            maxWidth: '1600px',
            mx: 'auto',
            width: '100%',
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* Chat Button - Premium Floating Action Button */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          left: { xs: 16, md: currentDrawerWidth + 24 },
          right: 24,
          zIndex: 1300,
          display: 'flex',
          justifyContent: 'flex-end',
          pointerEvents: 'none',
          transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Tooltip
          title={unreadCount > 0 ? `${unreadCount} new message${unreadCount > 1 ? 's' : ''}` : 'Chat with AI Assistant'}
          arrow
          placement="top"
          TransitionComponent={Zoom}
        >
          <Badge
            badgeContent={unreadCount > 0 ? unreadCount : 0}
            color="error"
            overlap="circular"
            sx={{
              pointerEvents: 'auto',
              '& .MuiBadge-badge': {
                fontSize: '0.75rem',
                fontWeight: 700,
                minWidth: 22,
                height: 22,
                padding: '0 6px',
                boxShadow: '0 4px 12px rgba(220, 38, 38, 0.5)',
                border: '2px solid white',
              },
            }}
          >
            <Fab
              color="primary"
              aria-label="AI Assistant"
              onClick={() => {
                setChatOpen(true);
                setUnreadCount(0);
              }}
              sx={{
                background: 'linear-gradient(145deg, #6366f1 0%, #8b5cf6 100%)',
                width: 60,
                height: 60,
                boxShadow: '0 12px 28px -8px rgba(99, 102, 241, 0.5)',
                border: '2px solid',
                borderColor: alpha('#ffffff', 0.2),
                '&:hover': {
                  background: 'linear-gradient(145deg, #4f46e5 0%, #7c3aed 100%)',
                  boxShadow: '0 16px 32px -8px rgba(99, 102, 241, 0.6)',
                  transform: 'scale(1.1) rotate(5deg)',
                  borderColor: alpha('#ffffff', 0.4),
                },
                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                '& .MuiSvgIcon-root': {
                  fontSize: 28,
                  transition: 'all 0.3s ease',
                },
              }}
            >
              <ChatIcon sx={{ color: 'white' }} />
            </Fab>
          </Badge>
        </Tooltip>
      </Box>

      {/* Chat Widget */}
      <ChatWidget
        open={chatOpen}
        onClose={() => {
          setChatOpen(false);
          setUnreadCount(0);
        }}
        onNewMessage={() => {
          if (!chatOpen) {
            setUnreadCount((prev) => prev + 1);
          }
        }}
      />
    </Box>
  );
};

export default Layout;